import { useCallback, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { API_SYNC_DELAY_MS, DEFAULT_INDEX_GROUP } from '@/config/constants';
import type { SymbolKey } from '@/entities/symbol';
import { pastExportsOptions } from '@/features/data-hub/api';
import { optionById } from '@/features/data-hub/constants/exportOptions';
import type {
  ExportRequestOverrides,
  ExportRequestParams,
  ExportSettingsType,
  ExportStatus
} from '@/features/data-hub/types';
import type { ExportItemType } from '@/features/data-hub/types/pastExports';
import { http } from '@/services/http';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';
import downloadFile from '@/shared/lib/download/downloadFile';

import { getFromToValues } from '@/features/data-hub/lib';
import { buildExportRequestBody } from '@/features/data-hub/lib/ExportRequestService';
import { clock } from '@/services/clock';

const EXPORT_ENDPOINT = 'export';
const EXCEL_EXPORT_TYPE = 'excel';

type ExportResponse = {
  downloadUrl?: string;
  message?: string;
};

const extractParamsFromUrl = (url: string) => {
  const parsed = new URL(url);
  const pathParts = parsed.pathname.split('/').filter(Boolean);
  const fileName = pathParts[1];
  const fileId = pathParts[2];
  return { fileName, fileId };
};

type UseExportMutationParams = {
  exportName: string | null;
  exportSettings: ExportSettingsType;
  symbols: SymbolKey[];
  userTimezone: string;
  userDateFormat: string;
  isForwardCurve: boolean;
  createExportName: () => string;
  setExportName: (name: string | null) => void;
  setPreviewOptionId: (id: string | null) => void;
};

const useExportMutation = ({
  exportName,
  exportSettings,
  symbols,
  userTimezone,
  userDateFormat,
  isForwardCurve,
  createExportName,
  setExportName,
  setPreviewOptionId
}: UseExportMutationParams) => {
  const [exportStatus, setExportStatus] = useState<ExportStatus>('idle');
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();

  const { fromValue, toValue } = getFromToValues(exportSettings);

  const postAndHandleResponse = async (requestBody: ExportRequestParams, snackbarKey: string) => {
    const response = await http.go.post<ExportResponse>(EXPORT_ENDPOINT, requestBody, {
      validateStatus: status => status === 200 || status === 202
    });

    if (response.status === 200 && response.data.downloadUrl) {
      const { fileId, fileName } = extractParamsFromUrl(response.data.downloadUrl);
      setExportStatus('idle');
      try {
        await downloadFile(fileId, fileName);
        showSuccessSnackbar('Export complete! File saved to your downloads.', snackbarKey);
      } catch {
        showErrorSnackbar(
          'Export completed, but the file could not be downloaded. Please try again from Past Exports.',
          5000,
          `${snackbarKey}-download-error`
        );
      }
    } else if (response.status === 202) {
      setExportStatus('queued');
    } else {
      setExportStatus('idle');
    }

    return response;
  };

  const { mutateAsync: exportDataMutation, isPending: loading } = useMutation({
    mutationFn: async (params: ExportRequestOverrides) => {
      const selectedOption = optionById.get(params.type);
      const exportSubType = params.exportSubType ?? selectedOption?.exportSubType ?? null;
      const isExcelExport = params.type === EXCEL_EXPORT_TYPE;

      const additionalParams =
        params.additionalParams ?? (isForwardCurve ? { fwdCurve: true as const } : undefined);

      const requestBody = buildExportRequestBody({
        id: params.id,
        name: params.name ?? exportName,
        exportType: params.type,
        exportSubType,
        from: params.from ?? fromValue,
        to: params.to ?? toValue,
        timezone: params.timezone ?? userTimezone,
        dateFormat: params.dateFormat ?? userDateFormat,
        includeMetadata: params.includeMetadata ?? exportSettings.includeMetadata,
        symbols: params.symbols ?? symbols,
        group: params.group ?? DEFAULT_INDEX_GROUP,
        additionalParams
      });

      if (isExcelExport) {
        setExportStatus('loading');
      }

      return postAndHandleResponse(requestBody, isExcelExport ? 'excel-export' : 'export');
    },
    onMutate: async (params: ExportRequestOverrides) => {
      await queryClient.cancelQueries({ queryKey: pastExportsOptions.lists() });

      const previousExports = queryClient.getQueryData<ExportItemType[]>(
        pastExportsOptions.lists()
      );

      if (params.id) {
        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old?.map(item =>
            item.id === params.id
              ? {
                  ...item,
                  exportStatus: 'ongoing',
                  savedAt: clock.now(),
                  lastExecutionTime: clock.now()
                }
              : item
          )
        );
      } else {
        const tempId = `temp-${clock.now()}`;
        const selectedOption = optionById.get(params.type);
        const optimisticAdditionalParams =
          params.additionalParams ?? (isForwardCurve ? { fwdCurve: true as const } : undefined);

        const newExport: ExportItemType = {
          id: tempId,
          name: params.name ?? exportName ?? 'New Export',
          exportType: params.type,
          exportSubType: params.exportSubType ?? selectedOption?.exportSubType ?? null,
          savedAt: clock.now(),
          lastExecutionTime: clock.now(),
          exportStatus: 'ongoing',
          exportStatusDetails: null,
          exportSettings: {
            from: params.from ?? fromValue,
            to: params.to ?? toValue,
            timezone: params.timezone ?? userTimezone,
            dateFormat: params.dateFormat ?? userDateFormat,
            includeMetadata: params.includeMetadata ?? exportSettings.includeMetadata
          },
          symbols: (params.symbols ?? symbols).map(s => ({
            key: {
              code: s.Code,
              periodType: s.PeriodType,
              timeRef: s.TimeRef,
              period: s.Period
            },
            group: params.group ?? DEFAULT_INDEX_GROUP
          })),
          ...(optimisticAdditionalParams && { additionalParams: optimisticAdditionalParams })
        };

        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old ? [newExport, ...old] : [newExport]
        );
      }

      return { previousExports };
    },
    onSuccess: () => {
      setExportName(createExportName());
      setPreviewOptionId(null);

      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: pastExportsOptions.lists() });
      }, API_SYNC_DELAY_MS);
    },
    onError: (_error, _params, context) => {
      if (context?.previousExports) {
        queryClient.setQueryData(pastExportsOptions.lists(), context.previousExports);
      }
      setExportStatus('idle');
      showErrorSnackbar(
        'Oops! Something went wrong. Please try again later.',
        3000,
        'export-error'
      );
    }
  });

  const resetExportStatus = useCallback(() => {
    setExportStatus('idle');
  }, []);

  return {
    exportDataMutation,
    loading,
    exportStatus,
    setExportStatus,
    resetExportStatus
  };
};

export default useExportMutation;
